'use client';

import { FC, useRef, useState } from 'react';

import { UserToggles } from '@ravago/shared/page-data/models/config';
import { NormalizedUnitOfMeasure } from '@ravago/shared/page-data/models/contentful-mapped';
import { CallOffOrderColor, ProductDetail, QuotedColor, SmallLotPricing } from '@ravago/shared/page-data/models/elements';
import { LeadTimeIndicator } from '@ravago/shared/page-data/models/elements/product-lead-time.model';
import { Box } from '@ravago/shared/radiance/components/box/components/Box/Box';
import { BreakpointSwitch } from '@ravago/shared/radiance/components/breakpoint-switch/components/BreakpointSwitch/BreakpointSwitch';
import { FlyoutActions, FlyoutContent } from '@ravago/shared/radiance/components/flyout/components/Flyout/Flyout';
import { Typography } from '@ravago/shared/radiance/components/typography/components/Typography/Typography';
import { useBreakpoint } from '@ravago/shared/radiance/hooks/useBreakpoint/useBreakpoint';
import { useTranslations } from '@ravago/shared/translations/client';

import { useSearchParams } from 'next/navigation';

import { OTHER_COLOR_ID, UNKNOWN_COLOR_ID } from '../constants';
import { useOrderNowState } from '../hooks/use-order-now-state';
import { useRequestForQuotationState } from '../hooks/use-request-for-quotation-state';
import { getDefaultExpanded } from '../utils/default-selection-utils';
import { Address } from '@ravago/shared/page-data/models/elements/address.model';

import { DynamicProductBlockMobile } from './dynamic-product-block-mobile';
import { QuoteFlyout } from './flyout/quote/quote-flyout';
import { RequestForQuotationFlyout } from './flyout/request-for-quotation/request-for-quotation-flyout';
import { OrderNowV2 } from './order-now-v2/order-now-v2';
import { OrderNow } from './order-now/order-now';
import { OrderNowActions } from './order-now/order-now-actions';
import { RequestForQuotation } from './request-for-quotation/request-for-quotation';
import { RequestForQuotationDesktop } from './request-for-quotation/request-for-quotation-desktop';
import { RfqButton } from './request-for-quotation/rfq-button';
import { RfqColorSelectorValue } from './request-for-quotation/rfq-color-selector';

/**
 * Determines which Order Now experience is rendered:
 * - 'order-now-v2': shown when the v2 flag is enabled AND the product has small-lot prices or customer colors (call-offs / quotes)
 * - 'order-now-v1': shown when the product has customer colors (call-offs / quotes) but v2 is not active
 * - 'none':         shown when there are no customer colors and no eligible small-lot prices
 */
type OrderNowMode = 'order-now-v2' | 'order-now-v1' | 'none';

type Props = {
   productDetail: ProductDetail | undefined;
   callOffOrderColors: CallOffOrderColor[];
   quotedColors: QuotedColor[];
   leadTimeIndicators: LeadTimeIndicator[];
   userToggles: UserToggles;
   usesAgreementApp: boolean;
   hasOrderNowPermission: boolean;
   leadTimeEnabled: boolean;
   leadTimeEnabledForQuotedColors: boolean;
   quoteDetailRoute: string;
   callOffAgreementRoute?: string;
   channelUOMs?: NormalizedUnitOfMeasure[];
   smallLotPrices: SmallLotPricing[];
   smallLotPricesEnabled: boolean;
   smallLotDefaultAddress?: Address;
   shouldUseOrderNowV2: boolean;
   bracketPricingEnabled: boolean;
   rfqType?: 'QUOTATION' | 'QUICK_QUOTE';
   initialColor?: RfqColorSelectorValue;
   hideExtraColorOptions?: boolean;
};

export const DynamicProductBlock: FC<Props> = ({
   callOffOrderColors,
   quotedColors,
   leadTimeIndicators,
   productDetail,
   userToggles,
   usesAgreementApp,
   hasOrderNowPermission,
   leadTimeEnabled,
   leadTimeEnabledForQuotedColors,
   quoteDetailRoute,
   callOffAgreementRoute,
   channelUOMs = [],
   smallLotPrices,
   smallLotPricesEnabled,
   smallLotDefaultAddress,
   shouldUseOrderNowV2,
   bracketPricingEnabled,
   rfqType = 'QUOTATION',
   initialColor,
   hideExtraColorOptions = false
}) => {
   const translate = useTranslations();
   const searchParams = useSearchParams();
   const flyoutContentRef = useRef<HTMLDivElement | null>(null);
   const { desktop } = useBreakpoint();

   const hasCustomerColors = callOffOrderColors.length > 0 || quotedColors.length > 0;

   const mode: OrderNowMode = (() => {
      if (shouldUseOrderNowV2 && ((smallLotPricesEnabled && smallLotPrices.length > 0) || hasCustomerColors)) return 'order-now-v2';
      if (hasCustomerColors) return 'order-now-v1';
      return 'none';
   })();

   const hasOrderNowBlock = mode !== 'none';

   const [expanded, setExpanded] = useState(() => getDefaultExpanded(hasOrderNowBlock, searchParams));

   const {
      value: rfqState,
      productDetail: productDetailForRfq,
      flyoutOpen: rfqFlyoutOpen,
      setValue: setRfqState,
      clearValue: clearRfqState,
      openFlyout: openRfqFlyout
   } = useRequestForQuotationState({ productDetail, initialColor });

   const {
      selectedCustomerColor,
      quantityAmount,
      selectedQuotationLineId,
      quoteFlyoutOpen,
      isAddingToCart,
      setSelectedCustomerColor,
      setQuantityAmount,
      setSelectedQuotationLineId,
      handleAddToCart,
      handleContinueCallOff,
      setQuoteFlyoutOpen
   } = useOrderNowState({ callOffOrderColors, quotedColors, usesAgreementApp });

   const handleExpandToggle = (group: 'order-now' | 'rfq') => {
      if (group === expanded) {
         setExpanded(group === 'order-now' ? 'rfq' : 'order-now');
      } else {
         setExpanded(group);
      }
   };

   const handleTabChange = (tab: 'order-now' | 'rfq') => {
      setExpanded(tab);
      flyoutContentRef.current?.scrollTo(0, 0);
   };

   const openRfq = () => handleExpandToggle('rfq');

   const canStartRfq =
      productDetailForRfq?.colors.length === 0 ||
      (!!rfqState.colorId &&
         (rfqState.producerColorCode !== undefined || rfqState.colorId === OTHER_COLOR_ID || rfqState.colorId === UNKNOWN_COLOR_ID));

   const handleRfqComplete = () => {
      clearRfqState();
      openRfqFlyout(false);
   };

   const orderNowContent =
      mode === 'order-now-v2' ? (
         <OrderNowV2
            callOffOrderColors={callOffOrderColors}
            quotedColors={quotedColors}
            smallLotPrices={smallLotPricesEnabled ? smallLotPrices : []}
            productName={productDetail?.description ?? ''}
            quoteDetailRoute={quoteDetailRoute}
            callOffAgreementRoute={callOffAgreementRoute}
            usesAgreementApp={usesAgreementApp}
            bracketPricingEnabled={bracketPricingEnabled}
            leadTimeIndicators={leadTimeIndicators}
            leadTimeEnabled={leadTimeEnabledForQuotedColors}
            smallLotDefaultAddress={smallLotDefaultAddress}
            onContinueCallOff={handleContinueCallOff}
            onRequestQuote={openRfq}
         />
      ) : (
         <OrderNow
            callOffOrderColors={callOffOrderColors}
            quotedColors={quotedColors}
            leadTimeIndicators={leadTimeIndicators}
            selected={selectedCustomerColor}
            quantityAmount={quantityAmount}
            selectedLineId={selectedQuotationLineId}
            leadTimeEnabled={leadTimeEnabledForQuotedColors}
            usesAgreementApp={usesAgreementApp}
            onSelectedChange={setSelectedCustomerColor}
            onSelectedLineChange={setSelectedQuotationLineId}
            onQuantityAmountChange={setQuantityAmount}
         />
      );

   const orderNowActions = (
      <OrderNowActions
         selected={selectedCustomerColor}
         quantityAmount={quantityAmount}
         selectedQuotationLineId={selectedQuotationLineId}
         isAddingToCart={isAddingToCart}
         onContinueCallOff={handleContinueCallOff}
         onAddToCart={handleAddToCart}
         onViewQuoteDetails={() => setQuoteFlyoutOpen(true)}
      />
   );

   const rfqContent = (
      <RequestForQuotation
         productDetail={productDetailForRfq}
         value={rfqState}
         leadTimeIndicators={leadTimeIndicators}
         userToggles={userToggles}
         leadTimeEnabled={leadTimeEnabled}
         onChange={setRfqState}
         hideExtraColorOptions={hideExtraColorOptions}
      />
   );

   const rfqActions = <RfqButton userToggles={userToggles} disabled={!canStartRfq} onClick={() => openRfqFlyout(true)} />;

   const canShowOrderNowContent = mode === 'order-now-v2' || hasOrderNowPermission;

   return (
      <>
         <BreakpointSwitch includeViews={['desktop']}>
            {hasOrderNowBlock && (
               <RequestForQuotationDesktop
                  title="request-for-quotation.order-now"
                  expanded={expanded === 'order-now'}
                  onExpandToggle={() => handleExpandToggle('order-now')}
               >
                  {canShowOrderNowContent ? (
                     <Box direction="column" gap="sm">
                        {orderNowContent}
                        {mode === 'order-now-v1' && orderNowActions}
                     </Box>
                  ) : (
                     <Typography>{translate('permissions.no-place-orders-permission')}</Typography>
                  )}
               </RequestForQuotationDesktop>
            )}

            <RequestForQuotationDesktop
               title="request-for-quotation.request-quote"
               expanded={expanded === 'rfq'}
               expandable={hasOrderNowBlock}
               onExpandToggle={() => handleExpandToggle('rfq')}
            >
               <Box direction="column" gap="sm">
                  {rfqContent}
                  {rfqActions}
               </Box>
            </RequestForQuotationDesktop>
         </BreakpointSwitch>

         {!desktop && (
            <DynamicProductBlockMobile
               hasCustomerColors={hasOrderNowBlock}
               productName={productDetail?.description}
               activeTab={expanded}
               onTabChange={handleTabChange}
            >
               <FlyoutContent ref={flyoutContentRef}>
                  {expanded === 'rfq' && rfqContent}
                  {expanded === 'order-now' &&
                     (canShowOrderNowContent ? orderNowContent : <Typography>{translate('permissions.no-place-orders-permission')}</Typography>)}
               </FlyoutContent>
               <FlyoutActions>
                  {expanded === 'rfq' && rfqActions}
                  {expanded === 'order-now' && canShowOrderNowContent && mode === 'order-now-v1' && orderNowActions}
               </FlyoutActions>
            </DynamicProductBlockMobile>
         )}

         {productDetail && (
            <RequestForQuotationFlyout
               userToggles={userToggles}
               open={rfqFlyoutOpen}
               productId={productDetail.id}
               productDescription={productDetail.description}
               isCustomerProduct={false}
               defaultProduct={productDetailForRfq}
               leadTimeIndicators={leadTimeIndicators}
               leadTimeEnabled={leadTimeEnabled}
               channelUOMs={channelUOMs}
               color={rfqState}
               type={rfqType}
               from="PRODUCT_DETAILS"
               onClose={() => openRfqFlyout(false)}
               onComplete={handleRfqComplete}
            />
         )}

         {productDetail && selectedCustomerColor && 'quotation' in selectedCustomerColor && (
            <QuoteFlyout
               product={productDetail}
               quotedColor={selectedCustomerColor}
               open={quoteFlyoutOpen}
               quoteDetailRoute={quoteDetailRoute}
               onFlyoutClose={() => setQuoteFlyoutOpen(false)}
            />
         )}
      </>
   );
};
